import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setAccountOpeningStep } from "../../redux/accountOpening";
import styles from "./style.module.css";
import { useNavigate } from "react-router";
import {
  accountOpeningSteps,
  daysArr,
  minorRelationshipStatus,
  monthsArr,
  returnBvnValidationErrorMessages,
} from "../../utils/constants";
import Tabs from "../Tabs/Tabs";
import CustomInputField from "../CustomInputField/CustomInputField";
import CustomDropdown from "../CustomDropdown";
import TermsAndConditions from "../TermsAndConditions";
import CustomModal from "../Modal/CustomModal";
import FormHelperText from "../FormHelperText";
import {
  BvnValidationErrorType,
  ValidateBVNType,
} from "../../types/validateBvn";
import {
  handleNumericInput,
  handleNumericPaste,
  minorYearslist,
  returnDateOfBirth,
  yearsList,
} from "../../utils/helpers";
import BvnValidationErrorPopup from "../BvnValidationErrorPopup";
import apiHooks from "../../customHooks/apis";
import { AcctLoginType } from "../../types/swiftSavings";

function BvnValidationDialog(props: any) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    useValidateBVNNinDob,
    useValidateAccBvnNin,
    useValidateIdentity,
    useUserDetails,
  } = apiHooks;
  const { handleUpdateUserDetails, clearUserDetails } = useUserDetails();
  const {
    handleValidateBvnNin,
    isValidatingBvnDob,
    apiResponse,
    bvnDobValidated,
  } = useValidateBVNNinDob();
  const {
    handleValidateIdentity,
    validatingIdentity,
    idValidationResponse,
    idValidated,
  } = useValidateIdentity();
  const { handleValidateAcctBvnNin, isValidatingAcct, errorSource } =
    useValidateAccBvnNin();
  const responseData = apiResponse || idValidationResponse;
  const {
    isFlagged,
    isSuccess,
    success,
    responseMessage = "",
    data,
  } = responseData || {};
  const [inputs, setInputs] = useState<ValidateBVNType>({});
  const [showModal, setShowModal] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [accountLogin, setAcountLogin] = useState<AcctLoginType>("bvn");
  const [bvnError, setBvnError] = useState<BvnValidationErrorType>("");
  const accountType = localStorage.getItem("altFinanceAccountType") || "";
  const dateOfBirth = useMemo(
    () =>
      returnDateOfBirth({
        day: inputs.day,
        month: inputs.month,
        year: inputs.year,
      }),
    [returnDateOfBirth, inputs.day, inputs.month, inputs.year]
  );

  const minorDateOfBirth = useMemo(
    () =>
      returnDateOfBirth({
        day: inputs.minorDay,
        month: inputs.minorMonth,
        year: inputs.minorYear,
      }),
    [returnDateOfBirth, inputs.minorDay, inputs.minorMonth, inputs.minorYear]
  );

  const loading = useMemo(
    () => isValidatingAcct || isValidatingBvnDob || validatingIdentity,
    [isValidatingAcct, isValidatingBvnDob, validatingIdentity]
  );
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    if (name === "relationship") {
      handleUpdateUserDetails({ [name]: value });
    }
    if ((name === "bvn" || name === "nin") && value.length > 11) return;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const checkFormValidity = () => {
    const isValidBvnNinLength = accountType?.includes("Swift")
      ? inputs?.[accountLogin]?.length === 11
      : inputs?.bvn?.length === 11 && inputs?.nin?.length === 11;
    const isValidMinorDob =
      !!inputs?.minorDay?.length &&
      !!inputs?.minorMonth?.length &&
      !!inputs?.minorYear?.length;
    const isValidOthersDob =
      !!inputs?.day?.length &&
      !!inputs?.month?.length &&
      !!inputs?.year?.length;
    const isValidDob = accountType?.includes("Minor")
      ? isValidMinorDob && isValidOthersDob && !!inputs?.relationship
      : isValidOthersDob;
    return isValidBvnNinLength && isValidDob;
  };

  const handleClickValidate = () => {
    if (checkFormValidity()) {
      handleUpdateUserDetails({ dateOfBirth, ...inputs });
      const { bvn, nin } = inputs;
      handleValidateAcctBvnNin(
        {
          bvn,
          nin,
          isSwiftAccount: accountType.toLowerCase().includes("swift"),
        },
        () => setBvnError("existing"),
        handleValidation
      );
    }
  };

  const handleAcceptTerms = (state: boolean) => {
    setAccepted(state);
    handleUpdateUserDetails({ termsCondition: state });
  };

  const removeGeneratedAccountNumbersFromLocalStorage = () => {
    localStorage.removeItem("generatedAccountNumber");
    localStorage.removeItem("generatedAccountNumberDom");
    localStorage.removeItem("generatedAccountNumberDomSavings");
  };

  const handleClickTab = (type: string) => {
    setAcountLogin(type.split(" ")[1].toLowerCase() as AcctLoginType);
    setInputs({ ...inputs, bvn: "", nin: "" });
  };

  const handleCloseModal = () => {
    !!bvnError && setBvnError("");
    !accepted ? navigate("/personal-account") : setShowModal(false);
  };

  const handleClickValidationErrorBtn = (btnText: string) => {
    handleCloseModal();
    btnText === "Proceed" && navigate("/");
  };

  const handleNextStep = useCallback(() => {
    if (bvnDobValidated || idValidated) {
      const lowerCaseMessage = responseMessage.toLowerCase();
      const isSuccessful = isSuccess || success;
      if (!isSuccessful) {
        if (lowerCaseMessage.includes("first name")) {
          return setBvnError("firstname");
        } else if (lowerCaseMessage.includes("date of birth")) {
          return setBvnError("dateofbirth");
        } else if (lowerCaseMessage.includes("gender")) {
          return setBvnError("gender");
        } else if (lowerCaseMessage.includes("last name")) {
          return setBvnError("lastname");
        } else if (lowerCaseMessage.includes("middle name")) {
          return setBvnError("middlename");
        }
      } else {
        dispatch(setAccountOpeningStep(accountOpeningSteps.otp));
        handleUpdateUserDetails({
          ...(data || responseData),
          dateOfBirth,
          ...inputs,
        });
      }
    }
  }, [responseMessage, isFlagged, isSuccess, bvnDobValidated, idValidated]);

  const handleValidation = () => {
    return accountType.includes("Swift")
      ? handleValidateBvnNin({
          payload: {
            [accountLogin === "bvn" ? accountLogin : "id"]:
              inputs[accountLogin],
            dateOfBirth,
          },
          type: accountLogin,
          callBack: handleNextStep,
        })
      : handleValidateIdentity(
          {
            bvn: inputs.bvn,
            nin: inputs.nin,
            dateOfBirth,
            minorDateOfBirth: minorDateOfBirth.replace("--", ""),
            isUpgradeValidation: false,
          },
          handleNextStep
        );
  };

  useEffect(() => {
    handleNextStep();
  }, [handleNextStep]);

  useEffect(() => {
    removeGeneratedAccountNumbersFromLocalStorage();
    // sessionStorage.clear();
    localStorage.removeItem("userDetails");
    clearUserDetails();
  }, []);

  return (
    <div>
      <div className={`card-form ${styles.bvn_card}`}>
        <div className="card-body">
          <h4 className="card-title mt-4">
            <b>Let’s open a {accountType} account for you</b>
          </h4>
          <p className="small_text">
            Enter your{" "}
            {accountType?.includes("Swift")
              ? "BVN or NIN and Date of birth"
              : accountType?.includes("Minor")
              ? "Guardian’s BVN, NIN and Date of Birth, Minor's Date of Birth and select relationship"
              : "BVN, NIN and Date of Birth"}{" "}
            to continue.
          </p>
          {accountType?.includes("Swift") && (
            <Tabs
              tabNames={["Use BVN", "Use NIN"]}
              onClickTab={handleClickTab}
              disabledTab="Use NIN"
            />
          )}
          <FormHelperText />
          <div className="form-group d-flex flex-column gap-3 mt-2">
            <CustomInputField
              required
              name={accountLogin}
              onChange={handleChange}
              onKeyDown={handleNumericInput}
              onPaste={handleNumericPaste}
              value={inputs[accountLogin] || ""}
              pattern="^\d+$"
              type="text"
              placeholder={`Enter your ${
                accountType?.includes("Minor") ? "guardian " : ""
              }${accountLogin?.toUpperCase()}`}
              label={`${
                accountType?.includes("Minor") ? "Guardian " : ""
              }${accountLogin?.toUpperCase()}`}
              info={
                accountLogin === "bvn"
                  ? [
                      "The Bank Verification Number (BVN) is an 11-digit number",
                      "Dial *565*0# to check your BVN",
                    ]
                  : [
                      "The National Identification Number (NIN) is an 11-digit number",
                      "Dial *346# to check your NIN",
                    ]
              }
            />
            {!accountType.includes("Swift") && (
              <CustomInputField
                required
                name={"nin"}
                onChange={handleChange}
                onKeyDown={handleNumericInput}
                onPaste={handleNumericPaste}
                value={inputs.nin || ""}
                pattern="^\d+$"
                type="text"
                placeholder={`Enter your ${
                  accountType.includes("Minor") ? "guardian " : ""
                }NIN`}
                label={`${accountType.includes("Minor") ? "Guardian " : ""}NIN`}
                info={[
                  "The National Identification Number (NIN) is an 11-digit number",
                  "Dial *346# to check your NIN",
                ]}
              />
            )}
          </div>
          <div className="mt-3">
            <div className="form-group d-flex flex-column flex-lg-row gap-2 align-items-end">
              <CustomDropdown
                required
                options={daysArr}
                placeholder="Day"
                onClickOption={handleChange}
                value={inputs.day || ""}
                name="day"
                label={`${
                  accountType.includes("Minor") ? "Guardian's " : ""
                }Date of birth`}
              />
              <CustomDropdown
                required
                options={monthsArr}
                placeholder="Month"
                onClickOption={handleChange}
                value={inputs.month || ""}
                name="month"
              />
              <CustomDropdown
                required
                options={[...yearsList].reverse()}
                placeholder="Year"
                onClickOption={handleChange}
                value={inputs.year || ""}
                name="year"
              />
            </div>
          </div>
          {accountType.includes("Minor") && (
            <>
              <div className="mt-3">
                <div className="form-group flex-column flex-lg-row d-flex gap-2 align-items-end">
                  <CustomDropdown
                    required
                    options={daysArr}
                    placeholder="Day"
                    onClickOption={handleChange}
                    value={inputs.minorDay || ""}
                    name="minorDay"
                    label="Minor's Date of birth"
                  />
                  <CustomDropdown
                    required
                    options={monthsArr}
                    placeholder="Month"
                    onClickOption={handleChange}
                    value={inputs.minorMonth || ""}
                    name="minorMonth"
                  />
                  <CustomDropdown
                    required
                    options={minorYearslist}
                    placeholder="Year"
                    onClickOption={handleChange}
                    value={inputs.minorYear || ""}
                    name="minorYear"
                  />
                </div>
              </div>
              <CustomDropdown
                required
                options={minorRelationshipStatus}
                label="Relationship Status"
                extraClasses={`mt-3`}
                placeholder="Select"
                name="relationship"
                key={bvnError}
                onClickOption={handleChange}
              />
            </>
          )}
          <div className="d-flex justify-content-end mt-4 gap-2">
            <button
              className="btn btn-filled-white btn-outline-gold"
              onClick={() => navigate("/personal-account")}
            >
              Previous
            </button>
            {loading ? (
              <div className="spinner-border text-gold" role="status">
                <span className="sr-only"></span>
              </div>
            ) : (
              <button
                className="btn btn-dange float-right btn-filled-red"
                onClick={handleClickValidate}
                disabled={!checkFormValidity()}
              >
                Validate
              </button>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        show={showModal || !!bvnError}
        onClickClose={handleCloseModal}
        modalTitle={
          bvnError
            ? returnBvnValidationErrorMessages(errorSource)[bvnError].title
            : "Terms And Conditions"
        }
      >
        {bvnError ? (
          <BvnValidationErrorPopup
            message={
              returnBvnValidationErrorMessages(errorSource)[bvnError].message
            }
            btnTexts={
              bvnError === "existing" ? ["Cancel", "Proceed"] : ["Okay"]
            }
            onClickBtn={handleClickValidationErrorBtn}
          />
        ) : (
          <TermsAndConditions
            onClickCheck={handleAcceptTerms}
            onClickProceed={handleCloseModal}
          />
        )}
      </CustomModal>
    </div>
  );
}

export default BvnValidationDialog;
